import _defineProperty from "/app/node_modules/.pnpm/next@13.4.17_@babel+core@7.14.3_@opentelemetry+api@1.4.1_react-dom@18.2.0_react@18.2.0_sass@1.49.9/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { takeLatest, takeEvery, put, spawn, delay } from 'redux-saga/effects';
import { call, select, all, apply } from 'typed-redux-saga';
import { unionBy } from 'lodash';
import { tracker } from 'libs/common/tracker';
import { userFilterItemsEvent, endOfListEvent, userPaginateCatalogEvent, userAppliedOrderEvent } from 'libs/common/event-tracker/events';
import { toUrlQuery, toParams } from 'libs/utils/url';
import { scrollToTop } from 'libs/utils/window';
import { getSearchStartData, getSearchStartDataFromUrl, removeSearchStartData, setSearchSessionData } from 'libs/utils/search';
import { transformCatalogDtos } from 'data/transformers/catalog';
import { transformBrandDtos, transformBrandDto } from 'data/transformers/brand';
import { transformSizeGroupDtos } from 'data/transformers/size-group';
import { transformDynamicFilterOptions, transformDynamicFilters, transformSelectedDefaultDynamicFilters, transformSelectedDynamicFilters } from 'data/transformers/dynamic-filter';
import * as api from 'data/api';
import * as abTestsSelectors from 'state/ab-tests/selectors';
import * as itemSelectors from 'state/items/selectors';
import { actions as itemActions } from 'state/items/slice';
import * as savedSearchesSelectors from 'state/saved-searches/selectors';
import { actions as savedSearchesActions } from 'state/saved-searches/slice';
import { getUserId } from 'state/session/selectors';
import { getIsFeatureSwitchEnabled } from 'state/feature-switches/selectors';
import { CATALOG_URL } from 'constants/routes';
import { SortByOption } from 'constants/filter';
import { CATALOG_PER_PAGE } from 'state/items/constants';
import * as statelessActions from './actions';
import { actions } from './slice';
import * as selectors from './selectors';
import { CATALOG_FILTERS_SEARCH_DEBOUNCE_AMOUNT, SCROLL_TO_TOP_DELAY, SUPPORTED_FILTERS } from './constants';
import { filterMatchingSizeGroups, filterMatchingSizeIds, buildCatalogUrlParams, getCatalogInitializersParamsFromUrl } from './utils';
const DEFAULT_PAGE_NUMBER = 1;
export function* fetchConfigurationData() {
  const response = yield* call(api.getSessionDefaults);

  if ('errors' in response) {
    yield put(actions.fetchConfigDataFailure());
    return;
  }

  yield put(actions.fetchConfigDataSuccess(response.session_defaults_configuration));
}
export function* fetchData(_ref) {
  let {
    payload
  } = _ref;
  const {
    relativeUrl,
    searchParams
  } = payload;
  yield put(actions.changeIsInCatalog(true));
  yield put(itemActions.setCatalogItemsPagination({
    time: Number(searchParams.time) || undefined,
    page: Number(searchParams.page) || DEFAULT_PAGE_NUMBER,
    perPage: CATALOG_PER_PAGE
  }));
  yield put(actions.fetchConfigDataRequest());
  const initializersParams = getCatalogInitializersParamsFromUrl(relativeUrl, searchParams);
  const response = yield* call(api.getCatalogInitializers, initializersParams);

  if ('errors' in response) {
    yield put(actions.fetchDataFailure());
    return;
  }

  const {
    dtos,
    filters
  } = response;
  const HARDCODED_STATIC_FILTERS = {
    brands: [],
    sizes: [],
    sizeGroups: []
  };
  yield put(actions.changeDtos({
    dtos: _objectSpread(_objectSpread({}, dtos), HARDCODED_STATIC_FILTERS)
  })); // TODO: hardcoded empty arrays should be removed when dynamic filters and redux data setting are scaled.

  const data = _objectSpread({
    catalogs: transformCatalogDtos(dtos.catalogs)
  }, HARDCODED_STATIC_FILTERS);

  if (dtos.dynamicFilters && dtos.selectedDynamicFilters) {
    data.dynamicFilters = transformDynamicFilters(dtos.dynamicFilters);
    data.selectedDynamicFilters = transformSelectedDynamicFilters(dtos.selectedDynamicFilters);
    data.supportedFilters = dtos.supportedFilters;
  }

  yield put(actions.fetchDataSuccess(data));
  yield put(actions.fetchDefaultDynamicFiltersSuccess({
    selectedDefaultFilters: transformSelectedDefaultDynamicFilters(dtos.selectedDefaultFilters || [])
  }));
  yield put(actions.setInitialFilters({
    filters
  }));
}
export function* fetchBrands(_ref2) {
  let {
    payload
  } = _ref2;
  yield delay(CATALOG_FILTERS_SEARCH_DEBOUNCE_AMOUNT);
  const response = yield* call(api.getBrands, {
    keyword: payload.query
  });

  if ('errors' in response) {
    yield put(statelessActions.fetchBrandsFailure());
    return;
  }

  yield put(actions.fetchBrandsSuccess({
    brands: transformBrandDtos(response.brands)
  }));
}
export function* fetchBrand(_ref3) {
  let {
    payload
  } = _ref3;
  const response = yield* call(api.getBrandById, payload.brandId);

  if ('errors' in response) {
    return;
  }

  const brand = transformBrandDto(response.brand);
  yield put(actions.fetchBrandSuccess({
    brand
  }));
}
export function* fetchDynamicFilterSearch(_ref4) {
  let {
    payload
  } = _ref4;
  yield delay(CATALOG_FILTERS_SEARCH_DEBOUNCE_AMOUNT);
  const filters = yield* select(selectors.getFilters);
  const selectedDynamicFilters = yield* select(selectors.getSelectedDynamicFilters);
  const response = yield* call(api.getCatalogSearchedFilters, _objectSpread(_objectSpread({}, filters), {}, {
    selectedDynamicFilters,
    filterSearchText: payload.text,
    filterSearchCode: payload.code
  }));

  if ('errors' in response) {
    yield put(statelessActions.fetchDynamicFilterSearchFailure());
    return;
  }

  yield put(actions.fetchDynamicFilterSearchSuccess({
    code: payload.code,
    options: transformDynamicFilterOptions(response.options),
    isSelectionHighlighted: response.is_selection_highlighted
  }));
}
export function* updateUrl(_ref5) {
  let {
    shouldReplaceHistory
  } = _ref5;
  const filters = yield* select(selectors.getFilters);
  const selectedDynamicFilters = yield* select(selectors.getSelectedDynamicFilters);
  const page = yield* select(itemSelectors.getCatalogItemCurrentPage);
  const perPage = yield* select(itemSelectors.getCatalogItemPerPage);
  const time = yield* select(itemSelectors.getCatalogItemTime);
  const currentSearchId = yield* select(savedSearchesSelectors.getCurrentSearchId);
  const currentUrlParams = toParams(window.location.search);

  const extraParams = _objectSpread(_objectSpread({}, currentUrlParams), {}, {
    search_id: currentSearchId
  });

  const urlParams = buildCatalogUrlParams({
    pagination: {
      page,
      perPage,
      time
    },
    extraParams,
    filters,
    selectedDynamicFilters
  });
  const urlQuery = "".concat(CATALOG_URL, "?").concat(toUrlQuery(urlParams));
  const historyStateAction = shouldReplaceHistory ? window.history.replaceState : window.history.pushState;
  yield call([window.history, historyStateAction], undefined, '', urlQuery);
}
export function* trackFilterChange(_ref6) {
  var _yield$select, _yield$select2;

  let {
    meta: {
      isPaginationEvent
    }
  } = _ref6;
  const filters = yield* select(selectors.getFilters);
  const selectedDynamicFilters = yield* select(selectors.getSelectedDynamicFilters);
  const searchCorrelationId = yield* select(selectors.getSearchCorrelationId);
  const searchSessionId = yield* select(selectors.getSearchSessionId);
  const globalSearchSessionId = yield* select(selectors.getGlobalSearchSessionId);
  const itemCount = (_yield$select = yield* select(itemSelectors.getCatalogItemTotalEntries)) !== null && _yield$select !== void 0 ? _yield$select : 0;
  const isEndReached = yield* select(itemSelectors.getCatalogItemEndReached);
  const currentPage = (_yield$select2 = yield* select(itemSelectors.getCatalogItemCurrentPage)) !== null && _yield$select2 !== void 0 ? _yield$select2 : 0;
  const perPage = yield* select(itemSelectors.getCatalogItemPerPage);
  const {
    searchStartId: startId,
    searchStartType: startType
  } = yield* call(getSearchStartData);
  const {
    searchStartId: startIdFromUrl,
    searchStartType: startTypeFromUrl
  } = yield* call(getSearchStartDataFromUrl);
  const searchStartId = startId || startIdFromUrl;
  const searchStartType = startType || startTypeFromUrl;
  const events = [];

  if (isPaginationEvent) {
    events.push(userPaginateCatalogEvent({
      currentPage,
      searchCorrelationId,
      searchSessionId,
      globalSearchSessionId
    }));
  } else {
    events.push(userFilterItemsEvent({
      itemCount,
      searchCorrelationId,
      searchSessionId,
      filters,
      globalSearchSessionId,
      searchStartId,
      searchStartType,
      selectedDynamicFilters
    }));
    if (searchStartId || searchStartType) yield* call(removeSearchStartData);

    if (filters.sortBy && filters.sortBy !== SortByOption.Relevance) {
      events.push(userAppliedOrderEvent({
        order: filters.sortBy,
        searchSessionId,
        globalSearchSessionId
      }));
    }
  }

  if (isEndReached) {
    events.push(endOfListEvent({
      itemCount,
      page: currentPage,
      perPage
    }));
  }

  yield all(events.map(event => apply(tracker, tracker.track, [event])));
}
export function* trackInitialLoad() {
  var _yield$select3;

  const filters = yield* select(selectors.getFilters);
  const selectedDynamicFilters = yield* select(selectors.getSelectedDynamicFilters);
  const searchCorrelationId = yield* select(selectors.getSearchCorrelationId);
  const searchSessionId = yield* select(selectors.getSearchSessionId);
  const globalSearchSessionId = yield* select(selectors.getGlobalSearchSessionId);
  const itemCount = (_yield$select3 = yield* select(itemSelectors.getCatalogItemTotalEntries)) !== null && _yield$select3 !== void 0 ? _yield$select3 : 0;
  const {
    searchStartId: startId,
    searchStartType: startType
  } = yield* call(getSearchStartData);
  const {
    searchStartId: startIdFromUrl,
    searchStartType: startTypeFromUrl
  } = yield* call(getSearchStartDataFromUrl);
  const searchStartId = startId || startIdFromUrl;
  const searchStartType = startType || startTypeFromUrl;
  const events = [userFilterItemsEvent({
    itemCount,
    searchCorrelationId,
    searchSessionId,
    filters,
    globalSearchSessionId,
    searchStartId,
    searchStartType,
    selectedDynamicFilters
  })];
  if (searchStartId || searchStartType) yield* call(removeSearchStartData);

  if (filters.sortBy && filters.sortBy !== SortByOption.Relevance) {
    events.push(userAppliedOrderEvent({
      order: filters.sortBy,
      searchSessionId,
      globalSearchSessionId
    }));
  }

  yield all(events.map(event => apply(tracker, tracker.track, [event])));
} // Since the initial brand data comes from general cache, we do not have accurate information
// about whether the current user has a selected brand favourited. As a result, it is
// important to update brand data to correctly render the UI.

export function* updateBrandForUserFlow() {
  const brandIds = yield* select(selectors.getBrandIds);
  const isBrandFilterVisible = yield* select(selectors.getIsBrandFilterVisible);
  const currentUserId = yield* select(getUserId);
  if (!currentUserId || brandIds.length !== 1 || !isBrandFilterVisible) return;
  yield put(statelessActions.fetchBrandRequest({
    brandId: brandIds[0]
  }));
}
export function* catalogFilterChange() {
  const isBrandFilterVisible = yield* select(selectors.getIsBrandFilterVisible);
  const isSizeFilterVisible = yield* select(selectors.getIsSizeFilterVisible);
  const brandIds = yield* select(selectors.getBrandIds);
  const sizeIds = yield* select(selectors.getSizeIds);
  const selectedCatalogSizeIds = yield* select(selectors.getSelectedCatalogSizeIds);

  if (brandIds.length && !isBrandFilterVisible) {
    yield put(actions.removeFilter({
      filter: {
        name: 'brandIds'
      }
    }));
  }

  if (sizeIds.length) {
    if (isSizeFilterVisible) {
      yield put(actions.sizeFilterSet({
        ids: selectedCatalogSizeIds
      }));
    } else {
      yield put(actions.removeFilter({
        filter: {
          name: 'sizeIds'
        }
      }));
    }
  }
}
export function* catalogFiltersChangeFlow() {
  const isInCatalog = yield* select(selectors.getIsInCatalog);
  if (!isInCatalog) return;
  const filters = yield* select(selectors.getFilters);
  yield put(actions.changeFilters({
    filters
  }));
}
export function getSupportedFilters(supportedFilterFlags) {
  const supportedFilters = [];
  if (supportedFilterFlags.materialFilter) supportedFilters.push(SUPPORTED_FILTERS.material);
  if (supportedFilterFlags.conditionFilter) supportedFilters.push(SUPPORTED_FILTERS.status);
  if (supportedFilterFlags.videoGameRatingFilter) supportedFilters.push(SUPPORTED_FILTERS.videoGameRating);
  if (supportedFilterFlags.colorFilter) supportedFilters.push(SUPPORTED_FILTERS.color);
  if (supportedFilterFlags.sizeFilter) supportedFilters.push(SUPPORTED_FILTERS.size);
  if (supportedFilterFlags.brandFilter) supportedFilters.push(SUPPORTED_FILTERS.brand);
  return supportedFilters;
}
export function* dynamicFiltersChangeFlow() {
  const isDynamicSizeFsEnabled = yield* select(getIsFeatureSwitchEnabled('dynamic_size_filter_web'));
  const dynamicSizeFilterAbTest = yield* select(abTestsSelectors.getAbTestByName('dynamic_size_filter'));
  const isDynamicSizeAbTestEnabled = (dynamicSizeFilterAbTest === null || dynamicSizeFilterAbTest === void 0 ? void 0 : dynamicSizeFilterAbTest.variant) === 'on';
  const isDynamicBrandFsEnabled = yield* select(getIsFeatureSwitchEnabled('dynamic_brand_filter_web'));
  const dynamicBrandFilterAbTest = yield* select(abTestsSelectors.getAbTestByName('dynamic_brand_filter'));
  const isDynamicBrandAbTestEnabled = (dynamicBrandFilterAbTest === null || dynamicBrandFilterAbTest === void 0 ? void 0 : dynamicBrandFilterAbTest.variant) === 'on';
  const isDynamicSizeEnabled = isDynamicSizeAbTestEnabled && isDynamicSizeFsEnabled;
  const isDynamicBrandEnabled = isDynamicBrandAbTestEnabled && isDynamicBrandFsEnabled;
  yield put(actions.fetchDynamicFiltersRequest({
    supportedFilters: getSupportedFilters({
      materialFilter: true,
      conditionFilter: true,
      videoGameRatingFilter: true,
      colorFilter: true,
      sizeFilter: isDynamicSizeEnabled,
      brandFilter: isDynamicBrandEnabled
    })
  }));
}
export function* temporaryDynamicFiltersChangeFlow() {
  const isDynamicSizeFsEnabled = yield* select(getIsFeatureSwitchEnabled('dynamic_size_filter_web'));
  const dynamicSizeFilterAbTest = yield* select(abTestsSelectors.getAbTestByName('dynamic_size_filter'));
  const isDynamicSizeAbTestEnabled = (dynamicSizeFilterAbTest === null || dynamicSizeFilterAbTest === void 0 ? void 0 : dynamicSizeFilterAbTest.variant) === 'on';
  const isDynamicBrandFsEnabled = yield* select(getIsFeatureSwitchEnabled('dynamic_brand_filter_web'));
  const dynamicBrandFilterAbTest = yield* select(abTestsSelectors.getAbTestByName('dynamic_brand_filter'));
  const isDynamicBrandAbTestEnabled = (dynamicBrandFilterAbTest === null || dynamicBrandFilterAbTest === void 0 ? void 0 : dynamicBrandFilterAbTest.variant) === 'on';
  const isDynamicSizeEnabled = isDynamicSizeAbTestEnabled && isDynamicSizeFsEnabled;
  const isDynamicBrandEnabled = isDynamicBrandAbTestEnabled && isDynamicBrandFsEnabled;
  yield put(actions.fetchTemporaryDynamicFiltersRequest({
    supportedFilters: getSupportedFilters({
      materialFilter: true,
      conditionFilter: true,
      videoGameRatingFilter: true,
      colorFilter: true,
      sizeFilter: isDynamicSizeEnabled,
      brandFilter: isDynamicBrandEnabled
    })
  }));
}
export function* filterRemoveFlow(_ref7) {
  let {
    payload
  } = _ref7;
  const {
    filter
  } = payload;
  if (!('value' in filter)) return;

  if (filter.name === 'catalogIds') {
    yield call(dynamicFiltersChangeFlow);
    yield call(catalogFilterChange);
  }
}
export function* setFiltersFlow(_ref8) {
  var _filters$catalogIds;

  let {
    payload
  } = _ref8;
  const {
    filters
  } = payload;
  if (!(filters !== null && filters !== void 0 && (_filters$catalogIds = filters.catalogIds) !== null && _filters$catalogIds !== void 0 && _filters$catalogIds.length)) return;
  yield call(dynamicFiltersChangeFlow);
  yield call(catalogFiltersChangeFlow);
}
export function* applyPersonalizedSizes() {
  const selectedCatalogs = yield* select(selectors.getSelectedCatalogs);
  const allSizeGroups = yield* select(selectors.getAllSizeGroups);
  const catalogSizeGroups = filterMatchingSizeGroups(allSizeGroups, selectedCatalogs);
  const currentSearchId = yield* select(savedSearchesSelectors.getCurrentSearchId); // We do not override saved search sizes

  if (currentSearchId) return;
  const response = yield* call(api.getUserSizes);

  if ('errors' in response) {
    yield put(statelessActions.applyPersonalizedSizesFailure());
    return;
  }

  const matchingSizeIds = selectedCatalogs.length ? filterMatchingSizeIds(catalogSizeGroups, response.size_ids) : response.size_ids;
  if (!matchingSizeIds.length) return;
  yield put(actions.applyPersonalizedSizesSuccess({
    ids: matchingSizeIds
  }));
}
export function* queryChange() {
  yield delay(SCROLL_TO_TOP_DELAY);
  yield call(scrollToTop);
}
export function* initialLoadWorker() {
  const searchSessionId = yield* select(selectors.getSearchSessionId);
  const globalSearchSessionId = yield* select(selectors.getGlobalSearchSessionId);
  yield* call(trackInitialLoad);
  yield* call(setSearchSessionData, {
    searchSessionId,
    globalSearchSessionId
  });
}
export function* getCatalogItemsSuccessWorker(action) {
  const {
    searchSessionId,
    globalSearchSessionId
  } = action.payload;
  yield* call(trackFilterChange, action);

  if (searchSessionId) {
    yield* call(setSearchSessionData, {
      searchSessionId
    });
  }

  if (globalSearchSessionId) {
    yield* call(setSearchSessionData, {
      globalSearchSessionId
    });
  }
}
export function* fetchDynamicFilters() {
  const filters = yield* select(selectors.getFilters);
  const selectedDynamicFilters = yield* select(selectors.getSelectedDynamicFilters);
  const supportedFilters = yield* select(selectors.getDynamicSupportedFilters);
  const filterSearchQuery = yield* select(selectors.getFilterSearchQuery);
  const time = yield* select(itemSelectors.getCatalogItemTime);
  const response = yield* call(api.getCatalogFilters, _objectSpread(_objectSpread({}, filters), {}, {
    selectedDynamicFilters,
    time,
    supportedFilters,
    filterSearchText: filterSearchQuery.text,
    filterSearchCode: filterSearchQuery.code
  }));

  if ('errors' in response) {
    yield put(actions.fetchDynamicFiltersFailure({
      errors: response.errors
    }));
  } else {
    yield put(actions.fetchDynamicFiltersSuccess({
      dynamicFilters: transformDynamicFilters(response.filters),
      selectedDynamicFilters: transformSelectedDynamicFilters(response.selected_filters)
    }));
  }
}
export function* fetchTemporaryDynamicFilters() {
  const filters = yield* select(selectors.getFilters);
  const temporarySelectedDynamicFilter = yield* select(selectors.getTemporarySelectedDynamicFilters);
  const selectedCatalogIds = yield* select(selectors.getTemporaryCatalogIds);
  const selectedSizeIds = yield* select(selectors.getTemporarySizeIds);
  const selectedBrandIds = yield* select(selectors.getTemporaryBrandIds);
  const selectedPriceRange = yield* select(selectors.getTemporaryPriceRange);
  const supportedFilters = yield* select(selectors.getDynamicSupportedFilters);
  const filterSearchQuery = yield* select(selectors.getFilterSearchQuery);
  const time = yield* select(itemSelectors.getCatalogItemTime);
  const response = yield* call(api.getCatalogFilters, _objectSpread(_objectSpread({}, filters), {}, {
    catalogIds: selectedCatalogIds,
    sizeIds: selectedSizeIds,
    brandIds: selectedBrandIds,
    priceFrom: selectedPriceRange.priceFrom,
    priceTo: selectedPriceRange.priceTo,
    selectedDynamicFilters: temporarySelectedDynamicFilter,
    filterSearchText: filterSearchQuery.text,
    filterSearchCode: filterSearchQuery.code,
    time,
    supportedFilters
  }));

  if ('errors' in response) {
    yield put(actions.fetchTemporaryDynamicFiltersFailure({
      errors: response.errors
    }));
  } else {
    yield put(actions.fetchTemporaryDynamicFiltersSuccess({
      dynamicFilters: transformDynamicFilters(response.filters),
      temporarySelectedDynamicFilters: transformSelectedDynamicFilters(response.selected_filters)
    }));
  }
}
export function* fetchDefaultDynamicFilters() {
  const selectedCatalogIds = yield* select(selectors.getCatalogIds);
  const selectedDynamicFilters = yield* select(selectors.getSelectedDynamicFilters);
  const response = yield* call(api.getCatalogDefaultFilters, {
    catalogIds: selectedCatalogIds
  });

  if ('errors' in response) {
    yield put(actions.fetchDefaultDynamicFiltersFailure({
      errors: response.errors
    }));
  } else {
    const defaultFilters = transformSelectedDefaultDynamicFilters(response.defaults || []);
    yield put(actions.fetchDefaultDynamicFiltersSuccess({
      selectedDefaultFilters: defaultFilters
    }));
    yield put(actions.setSelectedDynamicFilters({
      selectedDynamicFilters: unionBy(defaultFilters, selectedDynamicFilters, 'type')
    }));
  }
}
export function* fetchTemporaryDefaultDynamicFilters() {
  const temporarySelectedCatalogIds = yield* select(selectors.getTemporaryCatalogIds);
  const temporarySelectedDynamicFilters = yield* select(selectors.getTemporarySelectedDynamicFilters);
  const response = yield* call(api.getCatalogDefaultFilters, {
    catalogIds: temporarySelectedCatalogIds
  });

  if ('errors' in response) {
    yield put(actions.fetchDefaultDynamicFiltersFailure({
      errors: response.errors
    }));
  } else {
    const defaultFilters = transformSelectedDefaultDynamicFilters(response.defaults || []);
    yield put(actions.fetchDefaultDynamicFiltersSuccess({
      selectedDefaultFilters: defaultFilters
    }));
    yield put(actions.setTemporarySelectedDynamicFilters({
      temporarySelectedDynamicFilters: unionBy(defaultFilters, temporarySelectedDynamicFilters, 'type')
    }));
  }
}
export function* receiveData() {
  const isCatalogClientSideFetchingEnabled = yield* select(getIsFeatureSwitchEnabled('catalog_client_side_fetching'));
  if (isCatalogClientSideFetchingEnabled) return;
  const isInCatalog = yield* select(selectors.getIsInCatalog);
  if (!isInCatalog) return;
  yield put(actions.fetchConfigDataRequest());
  const dtos = yield* select(selectors.getDataDtos);
  const {
    sizes,
    sizeGroups
  } = transformSizeGroupDtos(dtos.sizeGroups);
  const data = {
    catalogs: transformCatalogDtos(dtos.catalogs),
    brands: transformBrandDtos(dtos.brands),
    sizes,
    sizeGroups
  };

  if (dtos.dynamicFilters && dtos.selectedDynamicFilters) {
    data.dynamicFilters = transformDynamicFilters(dtos.dynamicFilters);
    data.selectedDynamicFilters = transformSelectedDynamicFilters(dtos.selectedDynamicFilters);
    data.supportedFilters = dtos.supportedFilters;
  }

  yield put(actions.fetchDataSuccess(data));
  yield put(actions.fetchDefaultDynamicFiltersSuccess({
    selectedDefaultFilters: transformSelectedDefaultDynamicFilters(dtos.selectedDefaultFilters || [])
  }));
}
export default function* saga() {
  yield spawn(receiveData);
  yield takeLatest(actions.fetchConfigDataRequest, fetchConfigurationData);
  yield takeLatest(actions.fetchDataRequest, fetchData);
  yield takeLatest(statelessActions.fetchBrandsRequest, fetchBrands);
  yield takeEvery(statelessActions.fetchBrandRequest, fetchBrand);
  yield takeLatest(actions.fetchDynamicFiltersRequest, fetchDynamicFilters);
  yield takeLatest(actions.fetchTemporaryDynamicFiltersRequest, fetchTemporaryDynamicFilters);
  yield takeEvery(itemActions.getCatalogItemsSuccess, getCatalogItemsSuccessWorker);
  yield takeEvery(actions.removeFilter, filterRemoveFlow);
  yield takeEvery(statelessActions.applyPersonalizedSizesRequest, applyPersonalizedSizes);
  yield takeLatest(actions.catalogFilterSet, catalogFilterChange);
  yield takeLatest(actions.catalogFilterSet, fetchDefaultDynamicFilters);
  yield takeLatest(actions.setTemporaryCatalogIds, fetchTemporaryDefaultDynamicFilters);
  yield takeLatest(statelessActions.fetchDynamicFilterSearchRequest, fetchDynamicFilterSearch);
  yield takeLatest(actions.setQuery, queryChange);
  yield takeLatest(actions.setFilters, setFiltersFlow);
  yield takeLatest([actions.removeFilter, actions.removeFilters, actions.toggleBrandFilterOption, actions.toggleSizeFilterOption, actions.priceRangeFilterSet, actions.toggleSortFilterOption, actions.sizeFilterSet, actions.setQuery, actions.fetchDynamicFiltersSuccess], catalogFiltersChangeFlow);
  yield takeLatest([actions.submitSearchQuery, // relevant static filters actions
  actions.removeFilter, actions.toggleBrandFilterOption, actions.toggleSizeFilterOption, actions.priceRangeFilterSet, // dynamic filters actions
  actions.setSelectedDynamicFilter, actions.setSelectedDynamicFilters, actions.removeSelectedDynamicFilterId, actions.removeSelectedDynamicFilter, actions.removeSelectedDynamicFilters], dynamicFiltersChangeFlow); // adding or removing temporary dynamic filters will trigger new fetch

  yield takeLatest([actions.setTemporarySelectedDynamicFilter, actions.setTemporarySelectedDynamicFilters, actions.removeTemporarySelectedDynamicFilter, actions.removeTemporarySelectedDynamicFilterId, actions.removeTemporarySelectedDynamicFilters, // relevant temporary static filters actions
  actions.removeTemporaryCatalogIds, actions.setTemporarySizeIds, actions.removeTemporarySizeIds, actions.setTemporaryBrandIds, actions.removeTemporaryBrandIds, actions.setTemporaryPriceRange, actions.removeTemporaryPriceRange], temporaryDynamicFiltersChangeFlow);
  yield takeLatest([actions.changeFilters, itemActions.setCatalogItemsPage, itemActions.setCatalogItemsPerPage], updateUrl, {
    shouldReplaceHistory: false
  });
  yield takeLatest([actions.applyPersonalizedSizesSuccess, savedSearchesActions.setCurrentSearch], updateUrl, {
    shouldReplaceHistory: true
  });
  yield takeLatest([actions.changeFilters, actions.setInitialFilters, actions.fetchDataSuccess], updateBrandForUserFlow);
  yield takeEvery(actions.fetchDataSuccess, initialLoadWorker);
}